import { Paragraph, Button, DialogOverlayActions, DialogOverlayFooter, Separator, DialogOverlay } from '@fs/zion-ui'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useShowTemple } from '@fs/zion-permissions'
import ZionForm, { FormCheckbox, useZionForm } from '@fs/zion-form'
import { setPersonSharingAccepted } from '@fs/zion-tree-groups'
import { trackLink, trackEvent } from '@fs/zion-analytics'
import { NoticeLoading } from '@fs/zion-icon'
import { typeCompressor } from '@fs/zion-group-management'
import getTreeUrl from '../../helpers/getTreeUrl'
import acceptInvitation from '../../services/acceptInvitation'
import declineInvitation from '../../services/declineInvitation'
import TreeLegalTerms from '../TreeLegalTerms'

export default function JoinGroupDefaultOverlay({
  groupDetails,
  overlay,
  inviteId,
  setErrorMessage,
  maxGroupsPerUser,
  appHistory,
  inviteSingleUse,
  cisId,
  redirectUrl,
  setShowOnboardingOverlay,
  onJoinGroup,
  dialogTitle,
}) {
  const [loading, setLoading] = useState()

  const [t] = useTranslation()
  const [showTemple] = useShowTemple()

  const canShareOrdinances = showTemple && groupDetails.types.includes('ORDINANCE_SHARING')
  const canSharePersons = groupDetails.types.includes('PRIVATE_PERSON_SHARING')
  const isResearcherGroup = groupDetails.types.includes('RESEARCHER_TREE')

  let bodyText = t('group-management.chat-group-benefits.description')
  if (canShareOrdinances && canSharePersons) {
    bodyText = t('group-management.join.messages-ordinances-private-pedigree.description')
  } else if (canShareOrdinances) {
    bodyText = t('group-management.temple-group-benefits.description')
  } else if (canSharePersons) {
    bodyText = t('group-management.join.messages-private-pedigree.description')
  } else if (isResearcherGroup) {
    bodyText = t('group-management.join.messages-researcher.description')
  }

  const methods = useZionForm({
    mode: 'all',
    defaultValues: { agreeJoinCheckTree1: null, agreeJoinCheckTree2: null, agreeJoinCheck: null },
    resolver: (values) => {
      const errors = {}
      if (canSharePersons || isResearcherGroup) {
        if (!values.agreeJoinCheckTree1) errors.agreeJoinCheckTree1 = true
        if (!values.agreeJoinCheckTree2) errors.agreeJoinCheckTree2 = true
      } else if (!values.agreeJoinCheck) {
        errors.agreeJoinCheck = true
      }
      return { values, errors }
    },
  })

  const {
    formState: { isValid },
  } = methods

  const handleDecline = () => {
    if (inviteSingleUse) {
      // Even if there is an error, the user doesn't care and the admin can manually delete invite.
      declineInvitation(groupDetails.id, inviteId).catch((e) => {
        trackEvent({ name: `decline single invite:error:${inviteId}` })
        console.error(e)
      })
    }
    overlay.close()
  }

  const handleJoin = useCallback(() => {
    setLoading(true)
    acceptInvitation(groupDetails.id, inviteId)
      .then(async () => {
        trackLink({ name: 'accept invite submit:group:joined' })
        onJoinGroup?.()
        if (canSharePersons) {
          appHistory.replace({ search: '' })
          setShowOnboardingOverlay(true)
          await setPersonSharingAccepted(groupDetails.id, cisId, true)
          overlay.close()
        } else if (isResearcherGroup) {
          appHistory.replace({ search: '', pathname: '/research-tree' }, '')
          // eslint-disable-next-line promise/no-nesting -- Its conditionally called so its easier this way.
          return setPersonSharingAccepted(groupDetails.id, cisId, true).then(() => {
            if (redirectUrl) {
              window.location.assign(redirectUrl)
            } else {
              window.location.assign(getTreeUrl({ treeId: groupDetails.treeIds?.[0], pedigreeType: 'landscape' }))
            }
            return undefined
          })
        } else {
          overlay.close()
        }
        return null
      })
      .catch((error) => {
        setLoading(false)
        const warningHeader = error?.response?.headers?.warning
        if (warningHeader) {
          if (warningHeader.includes('40020')) {
            // User at group limit
            setErrorMessage(t('group-management.max-groups-reached.error', { maxGroups: maxGroupsPerUser }))
          } else if (warningHeader.includes('40021')) {
            // Group at member limit
            setErrorMessage(t('group-management.group-is-full.error'))
          } else if (warningHeader.includes('40310')) {
            // User banished
            setErrorMessage(t('group-management.user-banished.error'))
          } else if (warningHeader.includes('40401') || warningHeader.includes('41001')) {
            // Group not found or tombstoned
            setErrorMessage(t('group-management.not-active-group.error'))
          } else {
            // Unexpected error
            setErrorMessage(t('group-management.unable-to-join.error'))
          }
        } else {
          // Unexpected error
          setErrorMessage(t('group-management.unable-to-join.error'))
        }
      })
  }, [
    appHistory,
    canSharePersons,
    cisId,
    groupDetails.id,
    groupDetails.treeIds,
    inviteId,
    isResearcherGroup,
    maxGroupsPerUser,
    onJoinGroup,
    overlay,
    redirectUrl,
    setErrorMessage,
    setShowOnboardingOverlay,
    t,
  ])

  return (
    <DialogOverlay {...overlay} title={t(dialogTitle, { groupName: groupDetails.name })}>
      <ZionForm methods={methods}>
        <Paragraph size="sm">{bodyText}</Paragraph>

        {canSharePersons || isResearcherGroup ? (
          <TreeLegalTerms isResearcherGroup={isResearcherGroup} />
        ) : (
          <>
            <Separator size="xxs" />
            <FormCheckbox
              data-testid="agree-join-checkbox"
              name="agreeJoinCheck"
              label={t('group-management.join-group-privacy.checkbox')}
            />
          </>
        )}

        <DialogOverlayFooter>
          <DialogOverlayActions>
            <Button
              data-testid="join-group-overlay.decline.button"
              emphasis="low"
              linkName={`Join Group: No: GroupTypes:${typeCompressor(groupDetails.types.sort())}`}
              onClick={handleDecline}
            >
              {t('group-management.no-thanks.action')}
            </Button>
            <Button
              data-testid="join-group-overlay.join.button"
              emphasis="high"
              linkName={`Join Group: Yes: GroupTypes:${typeCompressor(groupDetails.types.sort())}`}
              disabled={!isValid || loading}
              Icon={loading ? NoticeLoading : undefined}
              onClick={handleJoin}
            >
              {t('group-management.yes-join.action')}
            </Button>
          </DialogOverlayActions>
        </DialogOverlayFooter>
      </ZionForm>
    </DialogOverlay>
  )
}
